import { useCallback, useSyncExternalStore } from 'react'
import {
  getPresentationVisibility,
  subscribePresentationVisibility,
} from '@/lib/presentation-visibility'

/** One scheduler per cadence/window, shared by every elapsed-time display. */
function createClock(intervalMs: number, background: boolean, phaseMs: number) {
  let now = Date.now()
  let timeout: ReturnType<typeof setTimeout> | undefined
  let unlisten: (() => void) | undefined
  const listeners = new Set<() => void>()
  const refresh = () => {
    clearTimeout(timeout)
    timeout = undefined
    if (!background && !getPresentationVisibility()) return
    now = Date.now()
    listeners.forEach(listener => listener())
    if (listeners.size)
      timeout = setTimeout(
        refresh,
        intervalMs - ((now - phaseMs + intervalMs) % intervalMs),
      )
  }
  return {
    getSnapshot: () => now,
    subscribe(listener: () => void) {
      listeners.add(listener)
      if (listeners.size === 1) {
        if (!background) unlisten = subscribePresentationVisibility(refresh)
        refresh()
      }
      return () => {
        listeners.delete(listener)
        if (!listeners.size) {
          clearTimeout(timeout)
          timeout = undefined
          unlisten?.()
          unlisten = undefined
        }
      }
    },
  }
}

const clocks = new Map<string, ReturnType<typeof createClock>>()
const idleSubscribe = () => () => {}

/** This clock drives presentation, never elapsed-time accounting or heartbeats. */
export function useDisplayClock({
  enabled = true,
  intervalMs = 1000,
  background = false,
  phaseMs = 0,
}: {
  enabled?: boolean
  intervalMs?: 1000 | 60000
  background?: boolean
  phaseMs?: number
} = {}) {
  // Bound the pool to 60 second-aligned phases for minute displays.
  const safePhase = Number.isFinite(phaseMs) ? phaseMs : 0
  const phase =
    (Math.ceil((((safePhase % intervalMs) + intervalMs) % intervalMs) / 1000) *
      1000) %
    intervalMs
  const key = `${intervalMs}:${background}:${phase}`
  let clock = clocks.get(key)
  if (!clock) {
    clock = createClock(intervalMs, background, phase)
    clocks.set(key, clock)
  }
  const getSnapshot = useCallback(
    () => (enabled ? clock.getSnapshot() : 0),
    [clock, enabled],
  )
  const now = useSyncExternalStore(
    enabled ? clock.subscribe : idleSubscribe,
    getSnapshot,
    () => 0,
  )
  // Return the snapshot as a dependency, so React Compiler also invalidates
  // memoized elapsed readouts on a tick. Subscription refreshes immediately.
  return enabled ? now : Date.now()
}
